import {
  DailyInternationalPowerMeterReport,
  dailyInternationalPowerMeterReportSchema,
} from '@sophocles/entities/DailyInternationalPowerMeterReport/dailyInternationalPowerMeterReportRecord';
import { mapListToRecords, normalizeList } from '@sophocles/entities/entityUtils';
import apiClient from '@sophocles/http-common';
import { checkHttpStatus, genericErrorAction } from '@sophocles/utils';
import { endOfDay, format, parseISO, startOfDay } from 'date-fns';
import { normalize } from 'normalizr';

/* Receive Actions */
export const RECEIVE_DAILY_INTERNATIONAL_POWER_METER_REPORTS = 'RECEIVE_DAILY_INTERNATIONAL_POWER_METER_REPORTS';
function receiveDailyInternationalPowerMeterReports(json) {
  return {
    type: RECEIVE_DAILY_INTERNATIONAL_POWER_METER_REPORTS,
    dailyInternationalPowerMeterReports: mapListToRecords(json, DailyInternationalPowerMeterReport),
  };
}

/* Request Actions */
// starter request
export const REQUEST_DAILY_INTERNATIONAL_POWER_METER_REPORT = 'REQUEST_DAILY_INTERNATIONAL_POWER_METER_REPORT';
function requestDailyInternationalPowerMeterReport(dailyInternationalPowerMeterReport_id) {
  return {
    type: REQUEST_DAILY_INTERNATIONAL_POWER_METER_REPORT,
    dailyInternationalPowerMeterReport_id,
  };
}

export const REQUEST_DAILY_INTERNATIONAL_POWER_METER_REPORTS_FOR_SITE_WITH_RANGE =
  'REQUEST_DAILY_INTERNATIONAL_POWER_METER_REPORTS_FOR_SITE_WITH_RANGE';
function requestDailyInternationalPowerMeterReportsForSiteWithRange(site_id, from, to) {
  return {
    type: REQUEST_DAILY_INTERNATIONAL_POWER_METER_REPORTS_FOR_SITE_WITH_RANGE,
    site_id,
    from,
    to,
  };
}

/* Fetch Actions */
// starter fetch

export function fetchDailyInternationalPowerMeterReport(dailyInternationalPowerMeterReport_id) {
  return dispatch => {
    dispatch(requestDailyInternationalPowerMeterReport(dailyInternationalPowerMeterReport_id));
    return apiClient
      .get(`/dailyinternationalpowermeterreports/${dailyInternationalPowerMeterReport_id}/`)
      .then(checkHttpStatus)
      .then(response =>
        normalizeList(response.data, 'dailyInternationalPowerMeterReports', dailyInternationalPowerMeterReportSchema),
      )
      .then(json =>
        dispatch(receiveDailyInternationalPowerMeterReports(json.entities.dailyInternationalPowerMeterReports)),
      )
      .catch(error => dispatch(genericErrorAction(error, REQUEST_DAILY_INTERNATIONAL_POWER_METER_REPORT)));
  };
}

export function fetchDailyInternationalPowerMeterReportsForSiteWithRange(site_id, from, to) {
  const fromTS = startOfDay(parseISO(from));
  const toTS = endOfDay(parseISO(to));
  return dispatch => {
    dispatch(requestDailyInternationalPowerMeterReportsForSiteWithRange(site_id, fromTS, toTS));
    return apiClient
      .get(
        `/dailyinternationalpowermeterreports/?site=${site_id}&report_date__gte=${format(
          fromTS,
          'yyyy-MM-dd',
        )}&report_date__lte=${format(toTS, 'yyyy-MM-dd')}`,
      )
      .then(checkHttpStatus)
      .then(response =>
        normalizeList(
          response.data.results,
          'dailyInternationalPowerMeterReports',
          dailyInternationalPowerMeterReportSchema,
        ),
      )
      .then(json =>
        dispatch(receiveDailyInternationalPowerMeterReports(json.entities.dailyInternationalPowerMeterReports)),
      )
      .catch(error =>
        dispatch(genericErrorAction(error, REQUEST_DAILY_INTERNATIONAL_POWER_METER_REPORTS_FOR_SITE_WITH_RANGE)),
      );
  };
}

/* Edit Actions */
export const EDIT_DAILY_INTERNATIONAL_POWER_METER_REPORT = 'EDIT_DAILY_INTERNATIONAL_POWER_METER_REPORT';
export function editDailyInternationalPowerMeterReport(dailyInternationalPowerMeterReport_id) {
  return {
    type: EDIT_DAILY_INTERNATIONAL_POWER_METER_REPORT,
    dailyInternationalPowerMeterReport: dailyInternationalPowerMeterReport_id,
  };
}

export function patchDailyInternationalPowerMeterReport(dailyInternationalPowerMeterReport_id, values) {
  return dispatch => {
    dispatch(editDailyInternationalPowerMeterReport(dailyInternationalPowerMeterReport_id));
    return apiClient
      .patch(`/dailyinternationalpowermeterreports/${dailyInternationalPowerMeterReport_id}/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, dailyInternationalPowerMeterReportSchema))
      .then(json =>
        dispatch(receiveDailyInternationalPowerMeterReports(json.entities.dailyInternationalPowerMeterReports)),
      )
      .catch(error => dispatch(genericErrorAction(error, EDIT_DAILY_INTERNATIONAL_POWER_METER_REPORT)));
  };
}

/* Create Actions */
export const CREATE_DAILY_INTERNATIONAL_POWER_METER_REPORT = 'CREATE_DAILY_INTERNATIONAL_POWER_METER_REPORT';
function sayCreateDailyInternationalPowerMeterReport(values) {
  return {
    type: CREATE_DAILY_INTERNATIONAL_POWER_METER_REPORT,
    values: values,
  };
}

export function createDailyInternationalPowerMeterReport(values) {
  return dispatch => {
    dispatch(sayCreateDailyInternationalPowerMeterReport(values));
    return apiClient
      .post(`/dailyinternationalpowermeterreports/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, dailyInternationalPowerMeterReportSchema))
      .then(json =>
        dispatch(receiveDailyInternationalPowerMeterReports(json.entities.dailyInternationalPowerMeterReports)),
      )
      .catch(error => dispatch(genericErrorAction(error, CREATE_DAILY_INTERNATIONAL_POWER_METER_REPORT)));
  };
}

/* Delete Actions */
export const DELETE_DAILY_INTERNATIONAL_POWER_METER_REPORT = 'DELETE_DAILY_INTERNATIONAL_POWER_METER_REPORT';
function sayDeleteDailyInternationalPowerMeterReport(dailyInternationalPowerMeterReport_id) {
  return {
    type: DELETE_DAILY_INTERNATIONAL_POWER_METER_REPORT,
    dailyInternationalPowerMeterReport: dailyInternationalPowerMeterReport_id,
  };
}

export function deleteDailyInternationalPowerMeterReport(dailyInternationalPowerMeterReport_id) {
  return dispatch =>
    apiClient
      .delete(`/dailyinternationalpowermeterreports/${dailyInternationalPowerMeterReport_id}/`)
      .then(checkHttpStatus)
      .then(r => dispatch(sayDeleteDailyInternationalPowerMeterReport(dailyInternationalPowerMeterReport_id)))
      .catch(error => dispatch(genericErrorAction(error, DELETE_DAILY_INTERNATIONAL_POWER_METER_REPORT)));
}
